import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Alert,
  Button,
  Fade,
  Slide,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Settings,
  Warehouse,
  NotificationsNone,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import { useFlashMessage } from '../../hooks/useFlashMessage';
import { getSelectedRfidDevice } from '../../utils/rfidDeviceUtils';
import Sidebar from './Sidebar';
import PageTransition from './PageTransition';

const DRAWER_WIDTH = 280;
const MOBILE_DRAWER_WIDTH = 260;

const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user, logout } = useAuth();
  const { getSelectedDevices } = useAppData();
  const { showWarning, message, type, isVisible, hideMessage } = useFlashMessage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Simulate loading state for page transitions
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  // Check device configuration and show flash message
  useEffect(() => {
    const selectedDevices = getSelectedDevices();
    const selectedHandheldDevice = getSelectedRfidDevice('audit') || '';
    
    const hasSelectedDevices = selectedDevices.length > 0;
    const hasHandheldDevice = selectedHandheldDevice !== '';
    
    // Only show message if we're not on the settings page
    const isOnSettingsPage = location.pathname === '/settings';
    
    if (!isOnSettingsPage && (!hasSelectedDevices || !hasHandheldDevice)) {
      const missingItems = [];
      if (!hasSelectedDevices) missingItems.push('device list');
      if (!hasHandheldDevice) missingItems.push('handheld device');
      
      showWarning(
        `Please configure your ${missingItems.join(' and ')} in Settings for optimal functionality.`
      );
    } else if (isOnSettingsPage) {
      hideMessage();
    }
  }, [getSelectedDevices, showWarning, hideMessage, location.pathname]);

  // Flash message component
  const FlashMessage = () => {
    if (!isVisible) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.3 }}
      >
        <Alert 
          severity={type === 'warning' ? 'warning' : type}
          icon={type === 'warning' ? <WarningIcon /> : <InfoIcon />}
          sx={{ 
            position: 'fixed',
            top: { xs: 56, sm: 64 },
            left: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            zIndex: 1300,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '& .MuiAlert-message': {
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }
          }}
          onClose={hideMessage}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => {
                navigate('/settings');
                hideMessage();
              }}
              sx={{ fontWeight: 600 }}
            >
              Go to Settings
            </Button>
          }
        >
          {message}
        </Alert>
      </motion.div>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Flash Message */}
      <FlashMessage />
      
      {/* App Bar */}
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { sm: `${DRAWER_WIDTH}px` },
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: (theme) => theme.zIndex.drawer + 1
          }}
        >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 3 }, minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { sm: 'none' },
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <img 
                  src="/img/logo.png" 
                  alt="BridgeX Logo" 
                  style={{
                    height: '32px',
                    width: 'auto',
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)'
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warehouse sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                  <Typography 
                    variant="h6" 
                    noWrap 
                    component="div" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Warehouse Management System
                  </Typography>
                  <Typography 
                    variant="h6" 
                    noWrap 
                    component="div" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                      display: { xs: 'block', sm: 'none' }
                    }}
                  >
                    BridgeX WMS
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {/* Notification Icon */}
            <IconButton
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <NotificationsNone />
            </IconButton>
            
            <Chip
              label={user?.location || 'Main Warehouse'}
              size="small"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontWeight: 500,
                display: { xs: 'none', sm: 'flex' },
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                }
              }}
            />
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <Avatar
                sx={{ 
                  width: { xs: 28, sm: 32 }, 
                  height: { xs: 28, sm: 32 }, 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
        </AppBar>
      </motion.div>

      {/* Profile Menu */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        TransitionComponent={undefined}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            minWidth: 200,
            mt: 1
          }
        }}
      >
        <Box sx={{ px: 3, py: 2, backgroundColor: '#f8fafc' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
            {user?.username}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            {user?.email}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
            {user?.role}
          </Typography>
        </Box>
        <Divider />
        <MenuItem 
          onClick={handleProfileMenuClose} 
          sx={{ 
            px: 3, 
            py: 1.5,
            '&:hover': { backgroundColor: '#f1f5f9' }
          }}
        >
          <AccountCircle fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
          <Typography variant="body2">Profile</Typography>
        </MenuItem>
        <MenuItem 
          onClick={handleProfileMenuClose} 
          sx={{ 
            px: 3, 
            py: 1.5,
            '&:hover': { backgroundColor: '#f1f5f9' }
          }}
        >
          <Settings fontSize="small" sx={{ mr: 2, color: '#64748b' }} />
          <Typography variant="body2">Settings</Typography>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            px: 3, 
            py: 1.5,
            color: '#dc2626',
            '&:hover': { backgroundColor: '#fef2f2' }
          }}
        >
          <Logout fontSize="small" sx={{ mr: 2 }} />
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: MOBILE_DRAWER_WIDTH,
              backgroundColor: '#ffffff',
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '4px 0 12px rgba(0, 0, 0, 0.1)'
            },
          }}
        >
          <Sidebar onClose={handleDrawerToggle} />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              backgroundColor: '#ffffff',
              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '4px 0 12px rgba(0, 0, 0, 0.1)'
            },
          }}
          open
        >
          <Sidebar />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          backgroundImage: { 
            xs: 'none', 
            sm: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)'
          }
        }}
      >
        <Toolbar />
        <PageTransition isLoading={isLoading} loadingText="Loading page...">
          {children}
        </PageTransition>
      </Box>
    </Box>
  );
};

export default DashboardLayout;